import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
} from '@fluentui/react-components';
import { getConfidenceLevel } from './confidence-levels';

const useStyles = makeStyles({
  helpContainer: {
    alignItems: 'center',
    columnGap: '5px',
    display: 'flex',
  },
  tableHeaderCell: {
    fontWeight: 'bold',
  },
});

export const OverviewTable = ({
  items,
  liftHelpTopic,
  liftLabel,
  primaryColumnHeader,
  primaryColumnHelpTopic,
  title,
}) => {
  const className = title.toLowerCase().replace(/ /g, '-');
  const i18n = useContext(I18nContext);
  const confidenceLabel = i18n.getString(_TL_('Confidence level'));
  const controlLabel = i18n.getString(_TL_('Control'));
  const defaultLiftLabel = i18n.getString(_TL_('Lift'));
  const treatmentLabel = i18n.getString(_TL_('Treatment'));
  const learnMore = i18n.getString(_TL_('Click to learn more about'));
  const styles = useStyles();
  const columns = [
    {
      columnKey: 'name',
      label: (
        <div className={styles.helpContainer}>
          <span>{primaryColumnHeader}</span>
          <HelpButton
            aria-label={`${learnMore} ${primaryColumnHeader}`}
            topic={primaryColumnHelpTopic}
          />
        </div>
      ),
    },
    {
      columnKey: 'control',
      label: (
        <div className={styles.helpContainer}>
          <span>{controlLabel}</span>
          <HelpButton
            aria-label={`${learnMore} ${controlLabel}`}
            topic="pop_BA_Experiments_Control"
          />
        </div>
      ),
    },
    {
      columnKey: 'treatment',
      label: (
        <div className={styles.helpContainer}>
          <span>{treatmentLabel}</span>
          <HelpButton
            aria-label={`${learnMore} ${treatmentLabel}`}
            topic="pop_BA_Experiments_Treatment"
          />
        </div>
      ),
    },
    {
      columnKey: 'lift',
      label: (
        <div className={styles.helpContainer}>
          <span>{liftLabel || defaultLiftLabel}</span>
          <HelpButton
            aria-label={`${learnMore} ${liftLabel || defaultLiftLabel}`}
            topic={liftHelpTopic}
          />
        </div>
      ),
    },
    {
      columnKey: 'confidence',
      label: (
        <div className={styles.helpContainer}>
          <span>{confidenceLabel}</span>
          <HelpButton
            aria-label={`${learnMore} ${confidenceLabel}`}
            topic="pop_BA_Experiments_Confidence"
          />
        </div>
      ),
    },
  ];
  const getPercentageString = useCallback((value) => {
    if (value === 'N/A') {
      return '--';
    }
    const percentageValue = i18n.formatDecimalToFixed(value * 100, { digits: 2 });
    const percentageString = i18n.getString(_TL_('{{value}}%'), {
      value: percentageValue,
    });
    return percentageString;
  }, [i18n]);

  return (
    <Table aria-label={title} className={`${className}-overview-table`}>
      <TableHeader>
        <TableRow>
          {columns.map(column => (
            <TableHeaderCell className={styles.tableHeaderCell} key={column.columnKey}>
              {column.label}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map(item => (
          <TableRow key={item.id}>
            <TableCell>{item.name}</TableCell>
            <TableCell>{getPercentageString(item.control)}</TableCell>
            <TableCell>{getPercentageString(item.treatment)}</TableCell>
            <TableCell>{getPercentageString(item.lift)}</TableCell>
            <TableCell>{i18n.getString(getConfidenceLevel(item.confidence))}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

OverviewTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    name: PropTypes.string.isRequired,
    confidence: PropTypes.string.isRequired,
    control: PropTypes.number.isRequired,
    lift: PropTypes.number.isRequired,
    treatment: PropTypes.number.isRequired,
  })).isRequired,
  liftHelpTopic: PropTypes.string.isRequired,
  liftLabel: PropTypes.string,
  primaryColumnHeader: PropTypes.string.isRequired,
  primaryColumnHelpTopic: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

OverviewTable.defaultProps = {
  liftLabel: null,
};
